import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';
import { PaymentMethodCard } from 'src/components/PaymentMethod';
import { BuyerCard } from 'src/components/Selectors/BuyerSelector/BuyerCard';
import { useAppContext } from 'src/contexts/AppContext';
import { useContent } from 'src/contexts/ContentContext';
import { Stack } from 'src/core/ui';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import { SectionContent } from 'src/modals/common/Detail/Detail.styled';
import { DetailSection } from 'src/modals/common/DetailSection';
import { Modal } from 'src/modals/Modal';
import { useRetailSaleContext } from 'src/modals/RetailSale/RetailSaleLocalContext';
import { ContentId } from 'src/utils/constants/contentId';

import { RetailSaleFormValues } from '../types';
import { PartialPaymentCoverageDialog } from './PartialPaymentCoverageDialog';
import { PartialPaymentInput } from './PartialPaymentInput';
import { RetailSaleEarningsSection } from './RetailSaleCheckoutStep.components';
import * as styles from './RetailSaleCheckoutStep.css';

export const RetailSaleCheckoutStep = () => {
  const {
    selectedBuyer,
    disabled,
    selectedStoredPaymentMethodId,
    setSelectedStoredPaymentMethodId,
    isPartialPayment,
    setIsPartialPayment,
    partialAmount,
    setPartialAmount,
    coverageDialogSaleId,
    onCoverageConfirmed,
    onCoverageDialogCancel,
  } = useRetailSaleContext();

  const { loginContext } = useAppContext();
  const { watch } = useFormContext<RetailSaleFormValues>();
  const totalNetProceeds = watch('totalNetProceeds');
  const marketplacePaymentLines = watch('marketplacePaymentLines');
  const currencyCode =
    watch('currencyCode') ??
    loginContext?.user?.activeAccount.currencyCode ??
    'USD';

  const totalAmount =
    (totalNetProceeds ?? 0) +
    (marketplacePaymentLines ?? []).reduce(
      (sum, l) => sum + (l.amount ?? 0),
      0
    );
  const customerInfoMsg = useContent(ContentId.CustomerInfo);
  const paymentMethodMsg = useContent(ContentId.PaymentMethod);
  const addNewCardMsg = useContent(ContentId.AddNewCard);

  const { data: savedPaymentMethods = [] } = useGetBuyerPaymentMethods(
    selectedBuyer?.buyerEmail
  );

  return (
    <Stack direction="column" gap="l">
      {/* Customer Info Card */}
      {selectedBuyer && (
        <DetailSection name={customerInfoMsg}>
          <SectionContent numOfColumns={1}>
            <BuyerCard buyer={selectedBuyer} />
          </SectionContent>
        </DetailSection>
      )}

      {/* EARNINGS section */}
      <RetailSaleEarningsSection disabled={disabled} />

      {/* PAYMENT METHOD section */}
      <DetailSection name={paymentMethodMsg}>
        <div className={styles.savedCardsSection}>
          {/* PARTIAL PAYMENT section */}
          <PartialPaymentInput
            disabled={disabled}
            totalAmount={totalAmount}
            currencyCode={currencyCode}
            isPartialPayment={isPartialPayment}
            partialAmount={partialAmount}
            onIsPartialPaymentChange={setIsPartialPayment}
            onPartialAmountChange={setPartialAmount}
          />
          {savedPaymentMethods.map((card) => {
            const cardId = card.storedPaymentMethodId;
            const isSelected = selectedStoredPaymentMethodId === cardId;
            return (
              <div
                key={cardId}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                className={clsx(styles.savedCardRow, {
                  [styles.savedCardRowSelected]: isSelected,
                })}
                onClick={
                  disabled
                    ? undefined
                    : () => setSelectedStoredPaymentMethodId(cardId)
                }
                onKeyDown={
                  disabled
                    ? undefined
                    : (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelectedStoredPaymentMethodId(cardId);
                        }
                      }
                }
              >
                <input
                  type="radio"
                  readOnly
                  checked={isSelected}
                  aria-hidden="true"
                />
                <PaymentMethodCard
                  cardBrand={card.cardBrand}
                  cardSummary={card.cardSummary}
                  expiryMonth={card.expiryMonth}
                  expiryYear={card.expiryYear}
                  isDefault={card.isDefault}
                />
              </div>
            );
          })}

          {/* Add a new card option */}
          <button
            type="button"
            onClick={() => {
              setSelectedStoredPaymentMethodId(null);
            }}
            className={clsx(styles.addNewCardRow, {
              [styles.addNewCardRowSelected]:
                selectedStoredPaymentMethodId === null,
            })}
          >
            <input
              type="radio"
              readOnly
              checked={selectedStoredPaymentMethodId === null}
              aria-hidden="true"
            />
            {addNewCardMsg}
          </button>
        </div>
      </DetailSection>
      {coverageDialogSaleId != null && (
        <Modal isOpen onClose={onCoverageDialogCancel}>
          <PartialPaymentCoverageDialog
            saleId={coverageDialogSaleId}
            partialAmount={partialAmount}
            currencyCode={currencyCode}
            onConfirm={onCoverageConfirmed}
            onCancel={onCoverageDialogCancel}
          />
        </Modal>
      )}
    </Stack>
  );
};
